import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Heart, Search, Menu, X, LogOut, Settings, Instagram, Youtube, User } from "lucide-react";
import { Logo } from "../Logo";
import { useLang } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import { useContentText } from "../../contexts/ContentContext";

const TikTokIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.93a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
  </svg>
);

export const Header = () => {
  const { t, lang, toggleLang } = useLang();
  const { user, signIn, signOut } = useAuth();
  const { count } = useCart();
  const { content, cl } = useContentText();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const location = useLocation();

  const navLink = (to, label, testid) => (
    <Link
      to={to}
      data-testid={testid}
      onClick={() => setOpen(false)}
      className={`text-[11px] uppercase tracking-[0.28em] font-display transition-colors ${
        location.pathname === to ? "text-[var(--monzal-gold)]" : "text-gray-300 hover:text-[var(--monzal-gold)]"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Utility bar */}
      <div className="bg-black/80 backdrop-blur-xl border-b border-white/5 hidden md:block">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 h-9 flex items-center justify-between text-[10px] tracking-[0.3em] font-mono-tech text-gray-400 uppercase">
          <div className="flex items-center gap-5">
            <span>◆ {cl("utility", "a", t("home.utilityA"))}</span>
            <span className="text-gray-600">·</span>
            <span>{cl("utility", "b", t("home.utilityB"))}</span>
            <span className="text-gray-600">·</span>
            <span>{cl("utility", "c", t("home.utilityC"))}</span>
          </div>
          <div className="flex items-center gap-5">
            <a href={content?.brand?.instagram_url || "https://instagram.com"} target="_blank" rel="noreferrer" className="hover:text-[var(--monzal-gold)] transition-colors" data-testid="social-instagram">
              <Instagram size={13} />
            </a>
            <a href={content?.brand?.youtube_url || "https://youtube.com"} target="_blank" rel="noreferrer" className="hover:text-[var(--monzal-gold)] transition-colors" data-testid="social-youtube">
              <Youtube size={14} />
            </a>
            <a href={content?.brand?.tiktok_url || "https://tiktok.com"} target="_blank" rel="noreferrer" className="hover:text-[var(--monzal-gold)] transition-colors" data-testid="social-tiktok">
              <TikTokIcon size={13} />
            </a>
            <div className="flex items-center gap-2 ml-2">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--monzal-emerald)] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--monzal-emerald)]"></span>
              </span>
              <span className="text-[var(--monzal-emerald)]">{cl("utility", "live", t("home.live"))}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-black/65 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 h-20 flex items-center justify-between gap-6">
          {/* Left cluster */}
          <div className="flex items-center gap-7 flex-1">
            <button onClick={toggleLang} data-testid="lang-toggle" className="hidden lg:flex items-center gap-1 text-[11px] uppercase tracking-[0.28em] font-display text-gray-300 hover:text-[var(--monzal-gold)] transition-colors">
              {lang.toUpperCase()} <span className="text-gray-600">▾</span>
            </button>
            <nav className="hidden lg:flex items-center gap-7">
              {navLink("/marketplace", t("nav.marketplace"), "nav-marketplace")}
              {navLink("/shop", t("nav.shop"), "nav-shop")}
              {navLink("/marketplace?type=rare", "Rare Lineage", "nav-rare")}
            </nav>
          </div>

          {/* Center logo */}
          <Link to="/" data-testid="home-logo-link" className="flex items-center justify-center shrink-0">
            <Logo size={42} />
          </Link>

          {/* Right cluster */}
          <div className="flex items-center gap-7 flex-1 justify-end">
            <nav className="hidden lg:flex items-center gap-7">
              {navLink("/breeders", t("nav.breeders"), "nav-breeders")}
              {navLink("/shop?cat=merch", t("nav.merch"), "nav-merch")}
              {user?.is_admin && navLink("/admin", t("nav.admin"), "nav-admin")}
            </nav>
            <div className="flex items-center gap-3">
              <button data-testid="nav-search" className="hidden sm:block text-gray-300 hover:text-[var(--monzal-gold)] p-2 transition-colors">
                <Search size={17} />
              </button>
              <Link to="/wishlist" data-testid="nav-wishlist" className="text-gray-300 hover:text-[var(--monzal-gold)] transition-colors p-2">
                <Heart size={17} />
              </Link>
              <Link to="/cart" data-testid="nav-cart" className="relative text-gray-300 hover:text-[var(--monzal-gold)] transition-colors p-2">
                <ShoppingBag size={17} />
                <span
                  data-testid="cart-count-badge"
                  className={`absolute -top-0 -right-0 text-[9px] font-mono-tech w-4 h-4 flex items-center justify-center rounded-full ${count > 0 ? "bg-[var(--monzal-gold)] text-black" : "bg-white/10 text-gray-400"}`}
                >
                  {count}
                </span>
              </Link>
              {user ? (
                <div className="relative">
                  <button
                    data-testid="user-menu-trigger"
                    onClick={() => setMenu(!menu)}
                    className="p-2 text-gray-300 hover:text-[var(--monzal-gold)] transition-colors"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full border border-[var(--monzal-gold)]/40" />
                    ) : (
                      <User size={17} />
                    )}
                  </button>
                  {menu && (
                    <div className="absolute right-0 mt-2 w-56 bg-[var(--monzal-surface)] border border-white/10" data-testid="user-menu">
                      <div className="px-4 py-3 border-b border-white/5">
                        <div className="text-xs text-gray-400 font-mono-tech truncate">{user.email}</div>
                        <div className="text-sm text-white font-display mt-1">{user.name}</div>
                      </div>
                      {user.is_admin && (
                        <Link to="/admin" onClick={() => setMenu(false)} data-testid="user-menu-admin" className="flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-wider text-gray-300 hover:text-[var(--monzal-gold)] hover:bg-white/5">
                          <Settings size={14} /> {t("nav.admin")}
                        </Link>
                      )}
                      <button data-testid="user-menu-signout" onClick={() => { signOut(); setMenu(false); }} className="flex items-center gap-2 w-full text-left px-4 py-3 text-xs uppercase tracking-wider text-gray-300 hover:text-[var(--monzal-gold)] hover:bg-white/5">
                        <LogOut size={14} /> {t("nav.signout")}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={signIn} data-testid="nav-signin" className="hidden sm:inline-flex text-[11px] uppercase tracking-[0.22em] font-display text-gray-300 hover:text-[var(--monzal-gold)] transition-colors ml-1">
                  {t("nav.signin")}
                </button>
              )}
              <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-white p-2 ml-1">
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/5 bg-black/95 backdrop-blur-xl">
          <div className="px-5 py-7 flex flex-col gap-6" data-testid="mobile-menu">
            {navLink("/marketplace", t("nav.marketplace"), "mobile-nav-marketplace")}
            {navLink("/shop", t("nav.shop"), "mobile-nav-shop")}
            {navLink("/marketplace?type=rare", "Rare Lineage", "mobile-nav-rare")}
            {navLink("/breeders", t("nav.breeders"), "mobile-nav-breeders")}
            {navLink("/shop?cat=merch", t("nav.merch"), "mobile-nav-merch")}
            {user?.is_admin && navLink("/admin", t("nav.admin"), "mobile-nav-admin")}
            <div className="flex items-center gap-3 pt-3 border-t border-white/5">
              <button onClick={toggleLang} data-testid="mobile-lang-toggle" className="text-[11px] uppercase tracking-[0.22em] font-display text-gray-400 border border-white/10 px-3 py-1.5">
                {lang.toUpperCase()} / {lang === "es" ? "EN" : "ES"}
              </button>
              {!user && (
                <button onClick={signIn} data-testid="mobile-signin" className="btn-gold ml-auto">
                  {t("nav.signin")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
